import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  Alert,
  Snackbar,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import { School as KitaIcon } from '@mui/icons-material';
import axios from 'axios';

const WEEKDAYS = [
  { key: 'monday', label: 'Montag' },
  { key: 'tuesday', label: 'Dienstag' },
  { key: 'wednesday', label: 'Mittwoch' },
  { key: 'thursday', label: 'Donnerstag' },
  { key: 'friday', label: 'Freitag' },
];

const DEFAULT_TIME = {
  start: new Date().setHours(8, 0),
  end: new Date().setHours(16, 0)
};

function Kita() {
  const [kitaData, setKitaData] = useState({
    name: 'Kita Sonnenschein',
    care_days: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
    },
    hp_ratio: {
      employees: 1,
      kids: 4
    },
    other_ratio: {
      employees: 1,
      kids: 8
    },
    hp_hours: {
      monday: { ...DEFAULT_TIME },
      tuesday: { ...DEFAULT_TIME },
      wednesday: { ...DEFAULT_TIME },
      thursday: { ...DEFAULT_TIME },
      friday: { ...DEFAULT_TIME },
    },
    other_hours: {
      monday: { start: new Date().setHours(7, 0), end: new Date().setHours(16, 0) },
      tuesday: { start: new Date().setHours(7, 0), end: new Date().setHours(16, 0) },
      wednesday: { start: new Date().setHours(7, 0), end: new Date().setHours(16, 0) },
      thursday: { start: new Date().setHours(7, 0), end: new Date().setHours(16, 0) },
      friday: { start: new Date().setHours(7, 0), end: new Date().setHours(16, 0) },
    }
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/api/kita-settings');
        if (response.data) {
          setKitaData({
            name: response.data.name,
            care_days: response.data.care_days,
            hp_ratio: {
              employees: response.data.hp_ratio_employees,
              kids: response.data.hp_ratio_kids
            },
            other_ratio: {
              employees: response.data.other_ratio_employees,
              kids: response.data.other_ratio_kids
            },
            hp_hours: response.data.hp_hours,
            other_hours: response.data.other_hours
          });
        }
      } catch (err) {
        console.error('Error fetching kita settings:', err);
        // Don't show error to user as we have default values
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      const response = await axios.put('/api/kita-settings', {
        name: kitaData.name,
        care_days: kitaData.care_days,
        hp_ratio_employees: kitaData.hp_ratio.employees,
        hp_ratio_kids: kitaData.hp_ratio.kids,
        other_ratio_employees: kitaData.other_ratio.employees,
        other_ratio_kids: kitaData.other_ratio.kids,
        hp_hours: kitaData.hp_hours,
        other_hours: kitaData.other_hours
      });

      if (response.data) {
        setSuccessMessage('Kita-Einstellungen erfolgreich gespeichert');
        setIsEditing(false);
      }
    } catch (err) {
      console.error('Error saving kita settings:', err);
      setError('Fehler beim Speichern der Einstellungen');
    }
  };

  const handleDayChange = (day) => {
    setKitaData({
      ...kitaData,
      care_days: {
        ...kitaData.care_days,
        [day]: !kitaData.care_days[day]
      }
    });
  };

  const Section = ({ title, children }) => (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
        {title}
      </Typography>
      {children}
      <Divider sx={{ mt: 3 }} />
    </>
  );

  const RatioInput = ({ label, value, onChange }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
      <TextField
        type="number"
        label="Betreuer"
        value={value.employees}
        onChange={(e) => onChange({ ...value, employees: parseInt(e.target.value) })}
        disabled={!isEditing}
        sx={{ width: 100 }}
      />
      <Typography>Betreuer für</Typography>
      <TextField
        type="number"
        label="Kinder"
        value={value.kids}
        onChange={(e) => onChange({ ...value, kids: parseInt(e.target.value) })}
        disabled={!isEditing}
        sx={{ width: 100 }}
      />
      <Typography>Kinder</Typography>
    </Box>
  );

  const TimeSettings = ({ day, label }) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" gutterBottom>{label}</Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" gutterBottom color="text.secondary">HP Kinder</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TimePicker
              label="Von"
              value={new Date(kitaData.hp_hours[day].start)}
              onChange={(newValue) => setKitaData({
                ...kitaData,
                hp_hours: {
                  ...kitaData.hp_hours,
                  [day]: { ...kitaData.hp_hours[day], start: newValue.getTime() }
                }
              })}
              disabled={!isEditing || !kitaData.care_days[day]}
              sx={{ width: 150 }}
            />
            <Typography>bis</Typography>
            <TimePicker
              label="Bis"
              value={new Date(kitaData.hp_hours[day].end)}
              onChange={(newValue) => setKitaData({
                ...kitaData,
                hp_hours: {
                  ...kitaData.hp_hours,
                  [day]: { ...kitaData.hp_hours[day], end: newValue.getTime() }
                }
              })}
              disabled={!isEditing || !kitaData.care_days[day]}
              sx={{ width: 150 }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" gutterBottom color="text.secondary">Weitere Kinder</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TimePicker
              label="Von"
              value={new Date(kitaData.other_hours[day].start)}
              onChange={(newValue) => setKitaData({
                ...kitaData,
                other_hours: {
                  ...kitaData.other_hours,
                  [day]: { ...kitaData.other_hours[day], start: newValue.getTime() }
                }
              })}
              disabled={!isEditing || !kitaData.care_days[day]}
              sx={{ width: 150 }}
            />
            <Typography>bis</Typography>
            <TimePicker
              label="Bis"
              value={new Date(kitaData.other_hours[day].end)}
              onChange={(newValue) => setKitaData({
                ...kitaData,
                other_hours: {
                  ...kitaData.other_hours,
                  [day]: { ...kitaData.other_hours[day], end: newValue.getTime() }
                }
              })}
              disabled={!isEditing || !kitaData.care_days[day]}
              sx={{ width: 150 }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  const ReadOnlyView = () => (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Paper sx={{ p: 4, mb: 2 }}>
          <Typography variant="h5" gutterBottom color="primary.main">{kitaData.name}</Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Betreuungstage</Typography>
              <Typography color="text.secondary">
                {WEEKDAYS
                  .filter(day => kitaData.care_days[day.key])
                  .map(day => day.label)
                  .join(', ')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Betreuungsschlüssel</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">HP Kinder</Typography>
                <Typography>{kitaData.hp_ratio.employees} Betreuer für {kitaData.hp_ratio.kids} Kinder</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Weitere Kinder</Typography>
                <Typography>{kitaData.other_ratio.employees} Betreuer für {kitaData.other_ratio.kids} Kinder</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom color="primary.main">Betreuungszeiten</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tag</TableCell>
                  <TableCell>HP Kinder</TableCell>
                  <TableCell>Weitere Kinder</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {WEEKDAYS.map((day) => (
                  <TableRow key={day.key}>
                    <TableCell>{day.label}</TableCell>
                    <TableCell>
                      {kitaData.care_days[day.key] ? 
                        `${new Date(kitaData.hp_hours[day.key].start).toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })} - ${new Date(kitaData.hp_hours[day.key].end).toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}` : 
                        '-'
                      }
                    </TableCell>
                    <TableCell>
                      {kitaData.care_days[day.key] ? 
                        `${new Date(kitaData.other_hours[day.key].start).toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })} - ${new Date(kitaData.other_hours[day.key].end).toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}` : 
                        '-'
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );

  const EditView = () => (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Paper sx={{ p: 4, mb: 2 }}>
          <Typography variant="h6" gutterBottom color="primary.main">Allgemeine Einstellungen</Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name der Kita"
                value={kitaData.name}
                onChange={(e) => setKitaData({ ...kitaData, name: e.target.value })}
                variant="outlined"
                sx={{ mb: 3 }}
              />
              
              <Typography variant="subtitle1" gutterBottom>Betreuungstage</Typography>
              <FormGroup>
                {WEEKDAYS.map((day) => (
                  <FormControlLabel
                    key={day.key}
                    control={
                      <Checkbox
                        checked={kitaData.care_days[day.key]}
                        onChange={() => handleDayChange(day.key)}
                      />
                    }
                    label={day.label}
                  />
                ))}
              </FormGroup>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Betreuungsschlüssel</Typography>
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>HP Kinder</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <TextField
                    type="number"
                    label="Betreuer"
                    value={kitaData.hp_ratio.employees}
                    onChange={(e) => setKitaData({
                      ...kitaData,
                      hp_ratio: { ...kitaData.hp_ratio, employees: parseInt(e.target.value) }
                    })}
                    sx={{ width: 120 }}
                  />
                  <Typography>für</Typography>
                  <TextField
                    type="number"
                    label="Kinder"
                    value={kitaData.hp_ratio.kids}
                    onChange={(e) => setKitaData({
                      ...kitaData,
                      hp_ratio: { ...kitaData.hp_ratio, kids: parseInt(e.target.value) }
                    })}
                    sx={{ width: 120 }}
                  />
                </Box>

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Weitere Kinder</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    type="number"
                    label="Betreuer"
                    value={kitaData.other_ratio.employees}
                    onChange={(e) => setKitaData({
                      ...kitaData,
                      other_ratio: { ...kitaData.other_ratio, employees: parseInt(e.target.value) }
                    })}
                    sx={{ width: 120 }}
                  />
                  <Typography>für</Typography>
                  <TextField
                    type="number"
                    label="Kinder"
                    value={kitaData.other_ratio.kids}
                    onChange={(e) => setKitaData({
                      ...kitaData,
                      other_ratio: { ...kitaData.other_ratio, kids: parseInt(e.target.value) }
                    })}
                    sx={{ width: 120 }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom color="primary.main">Betreuungszeiten</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tag</TableCell>
                    <TableCell>HP Kinder</TableCell>
                    <TableCell>Weitere Kinder</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {WEEKDAYS.map((day) => (
                    <TableRow key={day.key}>
                      <TableCell>{day.label}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TimePicker
                            label="Von"
                            value={new Date(kitaData.hp_hours[day.key].start)}
                            onChange={(newValue) => setKitaData({
                              ...kitaData,
                              hp_hours: {
                                ...kitaData.hp_hours,
                                [day.key]: { ...kitaData.hp_hours[day.key], start: newValue.getTime() }
                              }
                            })}
                            disabled={!kitaData.care_days[day.key]}
                            sx={{ width: 150 }}
                          />
                          <Typography>-</Typography>
                          <TimePicker
                            label="Bis"
                            value={new Date(kitaData.hp_hours[day.key].end)}
                            onChange={(newValue) => setKitaData({
                              ...kitaData,
                              hp_hours: {
                                ...kitaData.hp_hours,
                                [day.key]: { ...kitaData.hp_hours[day.key], end: newValue.getTime() }
                              }
                            })}
                            disabled={!kitaData.care_days[day.key]}
                            sx={{ width: 150 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TimePicker
                            label="Von"
                            value={new Date(kitaData.other_hours[day.key].start)}
                            onChange={(newValue) => setKitaData({
                              ...kitaData,
                              other_hours: {
                                ...kitaData.other_hours,
                                [day.key]: { ...kitaData.other_hours[day.key], start: newValue.getTime() }
                              }
                            })}
                            disabled={!kitaData.care_days[day.key]}
                            sx={{ width: 150 }}
                          />
                          <Typography>-</Typography>
                          <TimePicker
                            label="Bis"
                            value={new Date(kitaData.other_hours[day.key].end)}
                            onChange={(newValue) => setKitaData({
                              ...kitaData,
                              other_hours: {
                                ...kitaData.other_hours,
                                [day.key]: { ...kitaData.other_hours[day.key], end: newValue.getTime() }
                              }
                            })}
                            disabled={!kitaData.care_days[day.key]}
                            sx={{ width: 150 }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </LocalizationProvider>
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" color="text.primary">
          <KitaIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
          Kita-Einstellungen
        </Typography>
        <Button
          variant="contained"
          onClick={() => isEditing ? handleSave() : setIsEditing(true)}
        >
          {isEditing ? 'Speichern' : 'Bearbeiten'}
        </Button>
      </Box>

      {isEditing ? <EditView /> : <ReadOnlyView />}

      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Kita;